// Sayım detayı (/stok/sayim/:id): duruma göre taslak, sayım girişi, inceleme/onay ya da sonuç.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper,
  TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { useAuth } from '../../../../contexts/AuthContext';
import { CategoryService } from '../services/categoryService';
import { printCountSheet } from './stockCountPrint';
import { useTranslation } from 'react-i18next';
import {
  fmtDate, STATUS_COLOR, STATUS_LABEL, stockCountService, TYPE_LABEL, type StockCountDetail,
} from './stockCountService';
import CountingPanel from './CountingPanel';
import ReviewPanel from './ReviewPanel';
import NewStockCountDialog from './NewStockCountDialog';
import { FULL_STOCK_COUNT_PERMISSIONS, type StockCountUiPermissions } from './stockCountPermissions';

interface Props {
  /** Verilirse URL yerine bu sayım açılır (yönetici paneli). */
  countId?: string;
  /** Verilirse "Sayımlar" butonu sayfaya gitmek yerine bunu çağırır. */
  onBack?: () => void;
  perms?: StockCountUiPermissions;
}

const StockCountDetailPage: React.FC<Props> = ({ countId, onBack, perms = FULL_STOCK_COUNT_PERMISSIONS }) => {
  const params = useParams<{ id: string }>();
  const id = countId || params.id || '';
  const navigate = useNavigate();
  const goBack = () => (onBack ? onBack() : navigate('/stok/sayim'));
  const { i18n } = useTranslation();
  const tt = (tr: string, en: string) => ((i18n.language || '').startsWith('en') ? en : tr);

  const [count, setCount] = useState<StockCountDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [quickReview, setQuickReview] = useState(false);
  const { userDetails } = useAuth() as any;

  // A4 kağıt sayım listesi. Kategori adları önceden okunur; yazdırma penceresi tıklamayla
  // hemen açılmalı, yoksa tarayıcı açılır pencere engeline takılır.
  const [categoryNames, setCategoryNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!userDetails?.companyId) return;
    CategoryService.getAllCategories(userDetails.companyId)
      .then((cats) => setCategoryNames(Object.fromEntries(cats.map((c) => [c.id, c.name]))))
      .catch(() => setCategoryNames({}));
  }, [userDetails?.companyId]);
  const printSheet = () => {
    if (!count) return;
    try {
      printCountSheet(count, { categoryName: (cid) => categoryNames[cid] });
    } catch (e: any) {
      setError(e?.message || tt('Liste yazdırılamadı.', 'Could not print the list.'));
    }
  };

  const load = useCallback(async () => {
    setError(null);
    try {
      setCount(await stockCountService.get(id));
    } catch (e: any) {
      setError(e?.message || tt('Sayım yüklenemedi.', 'Count could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(e?.message || tt('İşlem yapılamadı.', 'Action failed.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!count) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={goBack}>{tt('Sayımlar', 'Counts')}</Button>
        <Alert severity="error" sx={{ mt: 2 }}>{error || tt('Sayım bulunamadı.', 'Count not found.')}</Alert>
      </Box>
    );
  }

  const isQuick = count.type === 'QUICK';
  const canCancel = (count.status === 'DRAFT' || count.status === 'COUNTING' || count.status === 'REVIEW')
    && (perms.canCreate || (isQuick && perms.canQuick));
  const showReview = count.status === 'REVIEW' || count.status === 'COMPLETED' || (isQuick && count.status === 'COUNTING' && quickReview);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, display: 'grid', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={goBack}>{tt('Sayımlar', 'Counts')}</Button>
        <Typography variant="h5" fontWeight={700}>{count.number}</Typography>
        {count.name && <Typography variant="h6" color="text.secondary">· {count.name}</Typography>}
        <Chip label={STATUS_LABEL[count.status]} color={STATUS_COLOR[count.status]} />
        <Box sx={{ flex: 1 }} />
        {count.status === 'DRAFT' && perms.canCreate && (
          <>
            <Button variant="outlined" onClick={() => setEditOpen(true)} disabled={busy}>{tt('Düzenle', 'Edit')}</Button>
            <Button variant="contained" onClick={() => act(() => stockCountService.start(count.id))} disabled={busy}>
              {tt('Sayımı başlat', 'Start count')}
            </Button>
          </>
        )}
        {count.status === 'COUNTING' && (
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={printSheet}>
            {tt('Kağıt liste yazdır', 'Print count sheet')}
          </Button>
        )}
        {count.status === 'COUNTING' && !isQuick && (
          <Button variant="contained" onClick={() => act(() => stockCountService.submit(count.id))} disabled={busy}>
            {tt('Sayımı incelemeye gönder', 'Send for review')}
          </Button>
        )}
        {count.status === 'COUNTING' && isQuick && (
          quickReview
            ? <Button variant="outlined" onClick={() => setQuickReview(false)}>{tt('Saymaya dön', 'Back to counting')}</Button>
            : (
              <>
                {/* Onaylayabilen kendisi onaylar; onay yetkisi yoksa yöneticiye gönderir. */}
                {perms.canApprove ? (
                  <Button variant="contained" onClick={() => setQuickReview(true)}>{tt('Kontrol et ve onayla →', 'Check and approve →')}</Button>
                ) : (
                  <Button variant="contained" onClick={() => act(() => stockCountService.submit(count.id))} disabled={busy}>
                    {tt('Yöneticiye onaya gönder', 'Send to manager for approval')}
                  </Button>
                )}
              </>
            )
        )}
        {canCancel && (
          <Button color="error" onClick={() => { setCancelReason(''); setCancelOpen(true); }} disabled={busy}>
            {tt('Sayımı iptal et', 'Cancel count')}
          </Button>
        )}
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Box><Typography variant="caption" color="text.secondary">{tt('Depo', 'Warehouse')}</Typography><Typography variant="body2" fontWeight={600}>{count.warehouseName}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">{tt('Tür', 'Type')}</Typography><Typography variant="body2" fontWeight={600}>{TYPE_LABEL[count.type]}{count.isBlind ? ` · ${tt('kör sayım', 'blind')}` : ''}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">{tt('Sayanlar', 'Counters')}</Typography>
          <Typography variant="body2" fontWeight={600}>
            {count.assignees.map((a) => `${a.userName || a.userUid}${a.status === 'SUBMITTED' ? ' ✓' : ''}`).join(', ') || '—'}
          </Typography>
        </Box>
        <Box><Typography variant="caption" color="text.secondary">{tt('Planlanan', 'Planned')}</Typography><Typography variant="body2" fontWeight={600}>{fmtDate(count.plannedStartAt)}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">{tt('Başladı', 'Started')}</Typography><Typography variant="body2" fontWeight={600}>{fmtDate(count.startedAt)}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">{tt('İlerleme', 'Progress')}</Typography><Typography variant="body2" fontWeight={600}>{count.countedLineCount} / {count.lineCount}</Typography></Box>
        {count.notes && <Box sx={{ flexBasis: '100%' }}><Typography variant="caption" color="text.secondary">{tt('Not', 'Note')}</Typography><Typography variant="body2">{count.notes}</Typography></Box>}
      </Paper>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {count.status === 'DRAFT' && (
        <Alert severity="info">
          {tt('Taslak sayımda stoğa dokunulmaz. "Sayımı başlat" denince kapsamdaki ürünlerin o anki miktarı kaydedilir ve sayım açılır.',
            'A draft does not touch stock. Starting the count records current quantities of the scoped products.')}
        </Alert>
      )}
      {count.status === 'CANCELLED' && <Alert severity="error">{tt('Bu sayım iptal edildi; stoğa dokunulmadı.', 'This count was cancelled; stock was not changed.')}</Alert>}
      {count.status === 'COUNTING' && !showReview && <CountingPanel count={count} onChanged={load} />}
      {showReview && <ReviewPanel key={`${count.status}-${quickReview}`} count={count} canApprove={perms.canApprove} onChanged={() => { setQuickReview(false); load(); }} />}

      <NewStockCountDialog open={editOpen} existing={count} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{tt('Sayımı iptal et', 'Cancel count')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>{tt('Sayım iptal edilir, stoğa dokunulmaz.', 'The count is cancelled; stock is not changed.')}</Typography>
          <TextField fullWidth size="small" label={tt('Neden (isteğe bağlı)', 'Reason (optional)')} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>{tt('Vazgeç', 'Back')}</Button>
          <Button color="error" variant="contained" onClick={() => { setCancelOpen(false); act(() => stockCountService.cancel(count.id, cancelReason)); }}>
            {tt('İptal et', 'Cancel count')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockCountDetailPage;
